import React from "react";
import { ArrowRightIcon, CheckIcon } from "../Icons";
import { Pricing, money } from "../business-case/calculator";
import { useLeadForm } from "../lead-form/LeadFormProvider";
import { CONTAINER, PrimaryLink, SecondaryButton, jumpTo } from "../ui";

export default function PricingHero({ pricing }: { pricing: Pricing }) {
  const openLeadForm = useLeadForm();

  return (
    <section className="tw-bg-[#f4f8fc] tw-pb-20 tw-pt-20 sm:tw-pb-12 sm:tw-pt-12">
      <div className={`${CONTAINER} tw-grid tw-grid-cols-[1.1fr_.9fr] tw-items-center tw-gap-14 sm:tw-grid-cols-1 sm:tw-gap-10`}>
        <div>
          <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.15em] tw-text-[#087bd3]">
            Simple, flexible pricing
          </p>
          <h1 className="tw-mt-5 tw-mb-0 tw-max-w-3xl tw-text-[clamp(2.5rem,4.4vw,3.75rem)] tw-font-black tw-leading-[1.03] tw-tracking-[-.045em] tw-text-[#10233f] sm:tw-text-[clamp(2rem,7vw,2.5rem)]">
            One platform. The level of support you need.
          </h1>
          <p className="tw-mt-6 tw-mb-0 tw-max-w-2xl tw-text-lg tw-leading-8 tw-text-[#536a82]">
            Start with a focused pilot, prove the programme and scale when you are ready. Add design,
            training, management and external mentors only when you need them.
          </p>

          <div className="tw-mt-8 tw-flex tw-flex-row tw-items-center tw-gap-3 sm:tw-flex-col sm:tw-items-stretch">
            <PrimaryLink href="#estimate" onClick={jumpTo("estimate")}>
              Build your estimate
              <ArrowRightIcon aria-hidden className="tw-h-4 tw-w-4 tw-shrink-0" />
            </PrimaryLink>
            <SecondaryButton type="button" onClick={() => openLeadForm({ enquiryType: "consultation" })}>
              Speak to our team
            </SecondaryButton>
          </div>
        </div>

        <div className="tw-rounded-[28px] tw-border tw-border-[#c9deef] tw-bg-white tw-p-8 tw-shadow-[0_24px_70px_rgba(23,72,111,.12)] sm:tw-p-6">
          <p className="tw-m-0 tw-text-xs tw-font-black tw-uppercase tw-tracking-[.12em] tw-text-[#087bd3]">
            Start with a focused pilot
          </p>
          <p className="tw-mt-4 tw-mb-0 tw-flex tw-items-baseline tw-gap-2 tw-text-5xl tw-font-black tw-tracking-[-.05em] tw-text-[#10233f]">
            {money(pricing.pilot.price)}
            <span className="tw-text-sm tw-font-bold tw-tracking-normal tw-text-[#5b7188]">
              {pricing.currency}
            </span>
          </p>
          <p className="tw-mt-3 tw-mb-0 tw-leading-7 tw-text-[#5b7188]">
            Six months · one fixed cohort · up to {pricing.pilot.maxParticipants} total participants
          </p>

          <dl className="tw-mt-6 tw-mb-0 tw-space-y-3 tw-border-t tw-border-[#dce8f2] tw-pt-5 tw-text-sm">
            <div>
              <dt className="tw-font-black tw-text-[#173754]">Included</dt>
              <dd className="tw-m-0 tw-text-[#5b7188]">Standard setup and administrator onboarding</dd>
            </div>
            <div>
              <dt className="tw-font-black tw-text-[#173754]">Use</dt>
              <dd className="tw-m-0 tw-text-[#5b7188]">
                Your own mentors, or add FutureLab mentors separately
              </dd>
            </div>
          </dl>

          <p className="tw-mt-5 tw-mb-0 tw-flex tw-items-start tw-gap-2 tw-text-xs tw-leading-5 tw-text-[#5b7188]">
            <CheckIcon aria-hidden className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-text-[#16a784]" />
            Administrator accounts do not count toward the participant allowance.
          </p>
        </div>
      </div>
    </section>
  );
}
